import React, { useState, useRef, useCallback, useEffect } from "react";
import { TelnyxRTC } from '@telnyx/webrtc';
import axios from 'axios';
import { getVoiceAudioConstraints, getHDVoiceCodecs } from '../../utils/audioQuality';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, CheckCircle, Link as LinkIcon, X, Phone, PhoneOff, AlertTriangle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TelnyxDialog = React.memo(function TelnyxDialog({
  open, onOpenChange, telnyxUser, telnyxSettings, setTelnyxSettings,
  telnyxLoading, telnyxSaving, testingConnection, connectionTestResults,
  testCallNumber, setTestCallNumber, testSmsNumber, setTestSmsNumber,
  testSmsMessage, setTestSmsMessage, testingCall, testingSms,
  onSave, onTestConnection, onTestCall, onTestSms
}) {
  const [webrtcCallActive, setWebrtcCallActive] = useState(false);
  const [webrtcCallState, setWebrtcCallState] = useState('idle'); // idle, connecting, ringing, active, ended
  const telnyxClientRef = useRef(null);
  const activeCallRef = useRef(null);

  // Cleanup WebRTC on dialog close
  useEffect(() => {
    if (!open) {
      hangupWebrtcCall();
    }
  }, [open]);

  const cleanupClient = useCallback(() => {
    // Disconnect client after a tick to avoid recursive notification loop
    const client = telnyxClientRef.current;
    telnyxClientRef.current = null;
    activeCallRef.current = null;
    setWebrtcCallActive(false);
    setWebrtcCallState('idle');
    // Clean up audio element
    const audioEl = document.getElementById('admin-test-call-audio');
    if (audioEl) { audioEl.srcObject = null; audioEl.remove(); }
    if (client) {
      setTimeout(() => {
        try { client.disconnect(); } catch (e) { /* ignore */ }
      }, 100);
    }
  }, []);

  const hangupWebrtcCall = useCallback(() => {
    try {
      if (activeCallRef.current) {
        activeCallRef.current.hangup();
      }
    } catch (e) { /* ignore */ }
    cleanupClient();
  }, [cleanupClient]);

  const handleWebrtcTestCall = useCallback(async () => {
    if (!testCallNumber.trim() || !telnyxUser) return;

    let toNumber = testCallNumber.trim().replace(/[-\s()]/g, '');
    if (!toNumber.startsWith('+')) toNumber = '+1' + toNumber;

    setWebrtcCallActive(true);
    setWebrtcCallState('connecting');

    try {
      // Get target user's WebRTC credentials
      const credRes = await axios.get(`${API}/telnyx/admin/webrtc-credentials/${telnyxUser.id}`);
      const { username, password } = credRes.data;

      if (!username || !password) {
        throw new Error('WebRTC credentials not configured for this user');
      }

      // Create TelnyxRTC client with target user's creds
      const client = new TelnyxRTC({
        login: username,
        password: password,
        ringtoneFile: null,
        ringbackFile: null,
        prefetchIceCandidates: true,
      });

      telnyxClientRef.current = client;

      client.on('telnyx.ready', () => {
        // Client connected, now make the call
        const audioConstraints = getVoiceAudioConstraints();
        const preferred_codecs = getHDVoiceCodecs();
        const call = client.newCall({
          destinationNumber: toNumber,
          callerNumber: telnyxSettings.phone_number || '',
          audio: audioConstraints,
          video: false,
          useStereo: false,
          ...(preferred_codecs ? { preferred_codecs } : {}),
        });
        activeCallRef.current = call;
      });

      client.on('telnyx.error', (error) => {
        console.error('Telnyx WebRTC error:', error);
        cleanupClient();
      });

      client.on('telnyx.notification', (notification) => {
        const call = notification.call;
        if (!call) return;

        switch (call.state) {
          case 'trying':
          case 'requesting':
            setWebrtcCallState('connecting');
            break;
          case 'ringing':
          case 'early':
            setWebrtcCallState('ringing');
            break;
          case 'active':
            setWebrtcCallState('active');
            // Attach remote audio
            if (call.remoteStream) {
              const audio = document.createElement('audio');
              audio.id = 'admin-test-call-audio';
              audio.srcObject = call.remoteStream;
              audio.autoplay = true;
              document.body.appendChild(audio);
            }
            break;
          case 'hangup':
          case 'destroy':
            cleanupClient();
            break;
          default:
            break;
        }
      });

      client.connect();
    } catch (error) {
      console.error('Test call failed:', error);
      const msg = error.response?.data?.detail || error.message || 'Failed to initiate test call';
      alert(msg);
      cleanupClient();
    }
  }, [testCallNumber, telnyxUser, telnyxSettings.phone_number, cleanupClient]);

  const callStateLabel = {
    connecting: 'Connecting...',
    ringing: 'Ringing...',
    active: 'Call Active',
    ended: 'Call Ended',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-green-600" />
            Setup Telnyx for {telnyxUser?.full_name}
          </DialogTitle>
          <DialogDescription>
            Configure Telnyx API credentials for click-to-call and SMS functionality.
          </DialogDescription>
        </DialogHeader>

        {telnyxLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-green-600" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> API Key is configured globally in system settings. Only user-specific settings are shown below.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telnyx-phone">Phone Number</Label>
              <Input
                id="telnyx-phone"
                type="tel"
                placeholder="+1234567890"
                value={telnyxSettings.phone_number}
                onChange={(e) => setTelnyxSettings(s => ({ ...s, phone_number: e.target.value }))}
                data-testid="telnyx-phone-input"
              />
              <p className="text-xs text-slate-500">Telnyx phone number for outbound calls/SMS</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telnyx-connection">Connection ID (Call Control App ID)</Label>
              <Input
                id="telnyx-connection"
                placeholder="1234567890"
                value={telnyxSettings.connection_id}
                onChange={(e) => setTelnyxSettings(s => ({ ...s, connection_id: e.target.value }))}
                data-testid="telnyx-connection-input"
              />
              <p className="text-xs text-slate-500">
                From Telnyx Portal &rarr; Call Control &rarr; Applications &rarr; Application ID
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telnyx-messaging">Messaging Profile ID (Optional)</Label>
              <Input
                id="telnyx-messaging"
                placeholder="xxxxx-xxxxx-xxxxx"
                value={telnyxSettings.messaging_profile_id}
                onChange={(e) => setTelnyxSettings(s => ({ ...s, messaging_profile_id: e.target.value }))}
                data-testid="telnyx-messaging-input"
              />
              <p className="text-xs text-slate-500">For SMS messaging profile</p>
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">WebRTC Credentials (Optional)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telnyx-webrtc-user">WebRTC Username</Label>
                  <Input
                    id="telnyx-webrtc-user"
                    placeholder="Username"
                    value={telnyxSettings.webrtc_username}
                    onChange={(e) => setTelnyxSettings(s => ({ ...s, webrtc_username: e.target.value }))}
                    data-testid="telnyx-webrtc-user-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telnyx-webrtc-pass">WebRTC Password</Label>
                  <Input
                    id="telnyx-webrtc-pass"
                    type="password"
                    placeholder="Leave blank to keep existing"
                    value={telnyxSettings.webrtc_password}
                    onChange={(e) => setTelnyxSettings(s => ({ ...s, webrtc_password: e.target.value }))}
                    data-testid="telnyx-webrtc-pass-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">For browser-based calling</p>
            </div>

            {/* Test Connection Button */}
            <div className="border-t pt-4 mt-4">
              <Button
                variant="outline"
                onClick={onTestConnection}
                disabled={testingConnection || !telnyxSettings.phone_number || !telnyxSettings.connection_id}
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="test-telnyx-connection-btn"
              >
                {testingConnection ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Testing Connection...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Test Telnyx Configuration
                  </>
                )}
              </Button>
              {(!telnyxSettings.phone_number || !telnyxSettings.connection_id) && (
                <p className="text-xs text-amber-600 mt-1">Fill phone number and connection ID first</p>
              )}
            </div>

            {/* Test Connection Results */}
            {connectionTestResults && (
              <div className={`border rounded-lg p-4 ${connectionTestResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-3">
                  {connectionTestResults.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold text-sm ${connectionTestResults.success ? 'text-green-900' : 'text-red-900'}`}>
                      {connectionTestResults.summary}
                    </h4>

                    {/* Validation Checks */}
                    <div className="mt-3 space-y-2">
                      {connectionTestResults.checks?.map((check, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs">
                          {check.status === 'ok' ? (
                            <CheckCircle className="h-3 w-3 text-green-600 mt-0.5" />
                          ) : (
                            <X className="h-3 w-3 text-red-600 mt-0.5" />
                          )}
                          <div>
                            <span className="font-medium">{check.name}:</span>{' '}
                            <span className={check.status === 'ok' ? 'text-green-800' : 'text-red-800'}>
                              {check.message}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Instructions */}
                    {connectionTestResults.instructions && connectionTestResults.instructions.length > 0 && (
                      <div className="mt-3 p-2 bg-white rounded border border-red-300">
                        <h5 className="font-semibold text-xs text-red-900 mb-2">Action Required:</h5>
                        <ul className="list-disc list-inside space-y-1 text-xs text-red-800">
                          {connectionTestResults.instructions.map((instruction, idx) => (
                            <li key={idx}>{instruction}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Webhook Instructions */}
                    {connectionTestResults.webhook_instructions && (
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-300">
                        <h5 className="font-semibold text-xs text-blue-900 mb-2 flex items-center gap-1">
                          <LinkIcon className="h-3 w-3" />
                          {connectionTestResults.webhook_instructions.title}
                        </h5>
                        <div className="space-y-0.5 text-xs text-blue-800">
                          {connectionTestResults.webhook_instructions.steps.map((step, idx) => (
                            <p key={idx} className="leading-relaxed">{step}</p>
                          ))}
                        </div>
                        {connectionTestResults.note && (
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded">
                            <p className="text-xs font-semibold text-amber-900">{connectionTestResults.note}</p>
                          </div>
                        )}
                        <div className="mt-2">
                          <p className="text-xs font-semibold text-blue-900 mb-1">DEPLOYED Webhook URL (Use this):</p>
                          <div className="p-2 bg-white rounded font-mono text-xs break-all border">
                            {connectionTestResults.webhook_url}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Test Call Section — WebRTC based */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Phone className="h-4 w-4 text-amber-600" />
                Test Call (WebRTC)
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="+1234567890"
                  value={testCallNumber}
                  onChange={(e) => setTestCallNumber(e.target.value)}
                  className="flex-1"
                  disabled={webrtcCallActive}
                  data-testid="test-call-number"
                />
                {webrtcCallActive ? (
                  <Button
                    variant="outline"
                    onClick={hangupWebrtcCall}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="test-call-hangup-btn"
                  >
                    <PhoneOff className="h-4 w-4 mr-1" />
                    Hang Up
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleWebrtcTestCall}
                    disabled={!testCallNumber.trim() || !telnyxSettings.webrtc_username}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    data-testid="test-call-btn"
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    Call
                  </Button>
                )}
              </div>
              {webrtcCallActive && (
                <div className="mt-2 flex items-center gap-2">
                  {webrtcCallState === 'active' ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      {callStateLabel[webrtcCallState]}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {callStateLabel[webrtcCallState] || webrtcCallState}
                    </span>
                  )}
                </div>
              )}
              {!telnyxSettings.webrtc_username && (
                <p className="text-xs text-amber-600 mt-1">WebRTC username required for test calls</p>
              )}
            </div>

            {/* Test SMS Section */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-600" />
                Test SMS
              </p>
              <div className="space-y-2">
                <Input
                  placeholder="Phone number (+1234567890)"
                  value={testSmsNumber}
                  onChange={(e) => setTestSmsNumber(e.target.value)}
                  data-testid="test-sms-number"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Test message..."
                    value={testSmsMessage}
                    onChange={(e) => setTestSmsMessage(e.target.value)}
                    className="flex-1"
                    data-testid="test-sms-message"
                  />
                  <Button
                    variant="outline"
                    onClick={onTestSms}
                    disabled={testingSms || !telnyxSettings.phone_number}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="test-sms-btn"
                  >
                    {testingSms ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-1" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {!telnyxSettings.phone_number && (
                <p className="text-xs text-blue-600 mt-1">Save phone number first</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={telnyxSaving || telnyxLoading}
            className="bg-green-600 hover:bg-green-700"
            data-testid="save-telnyx-btn"
          >
            {telnyxSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Phone className="mr-2 h-4 w-4" />
                Save Telnyx Settings
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default TelnyxDialog;
